import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Typography, Row, Col, Statistic, DatePicker, Space, Select, Spin, Alert, Tag } from 'antd';
import Table from 'components/Common/Table';
import { UserOutlined, TrophyOutlined } from '@ant-design/icons';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import axios from 'axios';
import useIsMobile from '../../hooks/useIsMobile';
import ReportFilterModal, { DatePresetButtons, MobileFilterButton, getDateRangeFromPreset } from '../../components/Reports/ReportFilterModal';
import ExportPdfButton from '../../components/Reports/ExportPdfButton';
import useUrlFilters from '../../hooks/useUrlFilters';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const TIER_COLORS = {
  PREMIUM: '#722ed1',
  STANDARD: '#1890ff',
  BASIC: '#52c41a',
  CUSTOM: '#fa8c16',
  Unknown: '#8c8c8c',
};

const CHART_COLORS = ['#1890ff', '#52c41a', '#722ed1', '#fa541c', '#faad14', '#13c2c2', '#eb2f96', '#2f54eb'];

// Filters live in the URL so a filtered report can be shared as a link.
// Relative presets are kept as the preset name; only custom and FY ranges carry dates.
const URL_FILTERS = {
  preset: { type: 'string', default: 'all' },
  range: { type: 'dateRange', keys: ['start_date', 'end_date'] },
  tier: { type: 'string', default: 'all' },
};
const PRESETS_WITH_DATES = ['custom', 'this_fy', 'last_fy'];
// Relative presets are recomputed from the name; a custom range with no dates is empty.
const resolveRange = (preset, range) =>
  (PRESETS_WITH_DATES.includes(preset) && range) || (preset === 'custom' ? null : getDateRangeFromPreset(preset));

const CustomerRevenueReport = () => {
  const isMobile = useIsMobile();
  const reportRef = useRef(null);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const filterPreset = filters.preset;
  const dateRange = useMemo(() => resolveRange(filterPreset, filters.range), [filterPreset, filters.range]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const tierFilter = filters.tier;
  const setTierFilter = (value) => setFilters({ tier: value });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const useDateRange = filterPreset === 'custom' || filterPreset === 'this_fy' || filterPreset === 'last_fy';
      const params = {
        filter_preset: useDateRange ? undefined : (filterPreset === 'all' ? 'all_time' : filterPreset),
        start_date: useDateRange && dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
        end_date: useDateRange && dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
      };

      if (tierFilter !== 'all') {
        params.tier = tierFilter;
      }

      const response = await axios.get('/accounts/account/revenue-analytics/', { params });

      if (response.status === 200) {
        setData(response.data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.error || 'Failed to fetch revenue data');
    } finally {
      setLoading(false);
    }
  }, [filterPreset, dateRange, tierFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle preset change using helper
  const handlePresetChange = (preset) => {
    setFilters({
      preset,
      range: PRESETS_WITH_DATES.includes(preset) ? getDateRangeFromPreset(preset) : null,
    });
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setFilters({ preset: 'custom', range: [dates[0], dates[1]] });
    } else {
      setFilters({ preset: 'all', range: null });
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({ preset: 'all', range: null });
    setTierFilter('all');
  };

  // Count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (filterPreset !== 'all') count++;
    if (tierFilter !== 'all') count++;
    return count;
  };

  const columns = [
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      sorter: (a, b) => a.customer.localeCompare(b.customer),
      fixed: isMobile ? false : 'left',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Tier',
      dataIndex: 'tier',
      key: 'tier',
      render: (tier) => (
        <Tag color={TIER_COLORS[tier] || 'default'}>{tier || 'Unknown'}</Tag>
      ),
      filters: data?.available_tiers?.map(t => ({ text: t.label, value: t.value })) || [],
      onFilter: (value, record) => record.tier === value,
      width: 100,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (val) => val || 'N/A',
      width: 180,
      ellipsis: true,
      responsive: ['md'],
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      render: (val) => `₹${val?.toLocaleString('en-IN')}`,
      sorter: (a, b) => a.revenue - b.revenue,
      align: 'right',
      width: 120,
    },
    {
      title: 'Invoices',
      dataIndex: 'invoice_count',
      key: 'invoice_count',
      sorter: (a, b) => a.invoice_count - b.invoice_count,
      align: 'right',
      width: 90,
      responsive: ['lg'],
    },
    {
      title: 'Avg Invoice',
      dataIndex: 'avg_invoice',
      key: 'avg_invoice',
      render: (val) => `₹${val?.toLocaleString('en-IN')}`,
      sorter: (a, b) => a.avg_invoice - b.avg_invoice,
      align: 'right',
      width: 110,
      responsive: ['md'],
    },
  ];

  // Tier table columns with responsive configuration
  const tierTableColumns = [
    {
      title: 'Tier',
      dataIndex: 'tier_label',
      key: 'tier_label',
      render: (label, record) => <Tag color={TIER_COLORS[record.tier] || 'default'}>{label}</Tag>,
      width: 100,
    },
    {
      title: 'Customers',
      dataIndex: 'customers',
      key: 'customers',
      align: 'right',
      width: 90,
    },
    {
      title: 'Invoices',
      dataIndex: 'invoices',
      key: 'invoices',
      align: 'right',
      width: 90,
      responsive: ['md'],
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      render: (v) => `₹${v?.toLocaleString('en-IN')}`,
      align: 'right',
      width: 120,
    },
    {
      title: 'Share',
      dataIndex: 'percentage',
      key: 'percentage',
      render: (v) => `${v}%`,
      align: 'right',
      width: 70,
    },
  ];

  // Prepare chart data
  const topCustomersChartData = data?.top_customers?.map((item, index) => ({
    name: item.customer?.length > (isMobile ? 10 : 15) ? item.customer.substring(0, isMobile ? 10 : 15) + '...' : item.customer,
    revenue: item.revenue,
    fill: CHART_COLORS[index % CHART_COLORS.length]
  })) || [];

  const tierPieData = data?.by_tier?.map((item, index) => ({
    name: item.tier_label || item.tier,
    value: item.revenue,
    fill: TIER_COLORS[item.tier] || CHART_COLORS[index % CHART_COLORS.length]
  })) || [];

  // Render additional filters for modal
  const renderAdditionalFilters = () => (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Customer Tier</Text>
        <Select
          value={tierFilter}
          onChange={setTierFilter}
          style={{ width: '100%' }}
          placeholder="Select Tier"
        >
          <Option value="all">All Tiers</Option>
          {data?.available_tiers?.map(tier => (
            <Option key={tier.value} value={tier.value}>{tier.label}</Option>
          ))}
        </Select>
      </div>
    </Space>
  );

  // Tables for the PDF export. Passed explicitly rather than scraped from the
  // rendered tables so the export carries every row, not just the page on screen.
  const pdfTables = [
    {
      title: 'Revenue by Tier',
      columns: [
        { header: 'Tier', key: 'tier_label' },
        { header: 'Customers', key: 'customers', align: 'right' },
        { header: 'Invoices', key: 'invoices', align: 'right' },
        { header: 'Revenue', key: 'revenue', align: 'right', format: 'currency' },
        { header: 'Share', key: 'percentage', align: 'right', format: 'percent' },
      ],
      rows: data?.by_tier || [],
    },
    {
      title: 'Customer Details',
      columns: [
        { header: 'Customer', key: 'customer' },
        { header: 'Tier', key: 'tier' },
        { header: 'Email', key: 'email' },
        { header: 'Revenue', key: 'revenue', align: 'right', format: 'currency' },
        { header: 'Invoices', key: 'invoice_count', align: 'right' },
        { header: 'Avg Invoice', key: 'avg_invoice', align: 'right', format: 'currency' },
      ],
      rows: data?.customers || [],
    },
  ];

  const pdfMeta = [
    { label: 'Total Revenue', value: `Rs. ${(data?.summary?.total_revenue || 0).toLocaleString('en-IN')}` },
    { label: 'Customers', value: data?.summary?.total_customers || 0 },
    { label: 'Top Customer', value: data?.summary?.top_customer || 'N/A' },
  ];

  return (
    <div ref={reportRef} style={{
      height: 'calc(100vh - 64px)',
      overflow: 'auto',
      padding: isMobile ? '12px' : '16px 24px',
      backgroundColor: '#f0f2f5'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <Title level={isMobile ? 4 : 2} style={{ margin: 0 }}>Customer Revenue Report</Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <ExportPdfButton
            reportRef={reportRef}
            title="Customer Revenue Report"
            subtitle={data?.summary?.date_range}
            meta={pdfMeta}
            tables={pdfTables}
            fileName="customer-revenue-report"
            disabled={loading || !data}
          />
          {isMobile && (
            <MobileFilterButton
              onClick={() => setFilterModalVisible(true)}
              activeFiltersCount={getActiveFiltersCount()}
            />
          )}
        </div>
      </div>

      {/* Desktop Filters */}
      {!isMobile && (
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <div>
              <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Date Range:</Text>
              <DatePresetButtons filterPreset={filterPreset} onPresetChange={handlePresetChange} />
            </div>
            <Space wrap size="middle">
              <div>
                <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Custom Date Range:</Text>
                <RangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  format="YYYY-MM-DD"
                  size="small"
                  allowClear={true}
                  placeholder={['Start Date', 'End Date']}
                />
              </div>
              <div>
                <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Customer Tier:</Text>
                <Select
                  value={tierFilter}
                  onChange={setTierFilter}
                  size="small"
                  style={{ width: 180 }}
                >
                  <Option value="all">All Tiers</Option>
                  {data?.available_tiers?.map(tier => (
                    <Option key={tier.value} value={tier.value}>{tier.label}</Option>
                  ))}
                </Select>
              </div>
            </Space>
          </Space>
        </Card>
      )}

      {/* Mobile Filter Modal */}
      <ReportFilterModal
        visible={filterModalVisible}
        onClose={() => setFilterModalVisible(false)}
        filterPreset={filterPreset}
        dateRange={dateRange}
        onChange={({ preset, range }) => setFilters({
          preset,
          range: PRESETS_WITH_DATES.includes(preset) ? range : null,
        })}
        onClearFilters={handleClearFilters}
        title="Customer Revenue Filters"
      >
        {renderAdditionalFilters()}
      </ReportFilterModal>

      {error && (
        <Alert message="Error" description={error} type="error" closable showIcon onClose={() => setError(null)} style={{ marginBottom: '16px' }} />
      )}

      {loading && (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}><Text>Loading revenue data...</Text></div>
          </div>
        </Card>
      )}

      {!loading && data && (
        <>
          {/* Summary Statistics */}
          <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
            <Col xs={12} sm={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Total Revenue</span>}
                  value={data.summary?.total_revenue || 0}
                  prefix="₹"
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px', color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Total Customers</span>}
                  value={data.summary?.total_customers || 0}
                  prefix={<UserOutlined />}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px', color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '11px' : '14px' }}>Avg Revenue/Customer</span>}
                  value={data.summary?.avg_revenue_per_customer || 0}
                  prefix="₹"
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '12px' }}>Top Customer</Text>
                </div>
                <div>
                  <TrophyOutlined style={{ color: '#faad14', marginRight: '8px' }} />
                  <Text strong style={{ fontSize: isMobile ? '12px' : '14px' }}>
                    {data.summary?.top_customer?.length > (isMobile ? 12 : 20)
                      ? data.summary.top_customer.substring(0, isMobile ? 12 : 20) + '...'
                      : data.summary?.top_customer || 'N/A'}
                  </Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '13px' }}>
                    ₹{data.summary?.top_customer_revenue?.toLocaleString('en-IN') || 0}
                  </Text>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Charts Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
            {/* Top Customers Bar Chart */}
            <Col xs={24} md={14}>
              <Card size="small" title={<Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>Top 10 Customers by Revenue</Text>}>
                {topCustomersChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                    <BarChart
                      data={topCustomersChartData}
                      layout="vertical"
                      margin={{ top: 20, right: isMobile ? 10 : 30, left: isMobile ? 60 : 80, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fontSize: isMobile ? 10 : 11 }} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        tick={{ fontSize: isMobile ? 10 : 11 }}
                        width={isMobile ? 60 : 80}
                      />
                      <Tooltip formatter={(value) => `₹${value?.toLocaleString('en-IN')}`} />
                      <Bar dataKey="revenue" name="Revenue">
                        {topCustomersChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}><Text type="secondary">No customer data available</Text></div>
                )}
              </Card>
            </Col>

            {/* Revenue by Tier Pie Chart */}
            <Col xs={24} md={10}>
              <Card size="small" title={<Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>Revenue by Customer Tier</Text>}>
                {tierPieData.length > 0 && tierPieData.some(d => d.value > 0) ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                    <PieChart>
                      <Pie
                        data={tierPieData.filter(d => d.value > 0)}
                        cx="50%"
                        cy="50%"
                        innerRadius={isMobile ? 40 : 50}
                        outerRadius={isMobile ? 70 : 90}
                        paddingAngle={2}
                        dataKey="value"
                        label={isMobile
                          ? ({ name, percent }) => `${(percent * 100).toFixed(0)}%`
                          : ({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`
                        }
                        labelLine={{ length: isMobile ? 5 : 10, length2: isMobile ? 3 : 5 }}
                      >
                        {tierPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `₹${value?.toLocaleString('en-IN')}`} />
                      {isMobile && <Legend />}
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}><Text type="secondary">No tier data available</Text></div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Monthly Trend Chart */}
          <Card
            size="small"
            title={<Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>Monthly Revenue Trend</Text>}
            style={{ marginBottom: '16px' }}
          >
            {data.monthly_trend && data.monthly_trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
                <LineChart
                  data={data.monthly_trend}
                  margin={{ top: 20, right: isMobile ? 10 : 30, left: isMobile ? 0 : 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: isMobile ? 10 : 11 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tick={{ fontSize: isMobile ? 10 : 11 }}
                    width={isMobile ? 50 : 60}
                    tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`}
                  />
                  <Tooltip formatter={(value, name) => name === 'revenue' ? `₹${value?.toLocaleString('en-IN')}` : value} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#52c41a"
                    strokeWidth={2}
                    name="Revenue"
                    dot={{ r: isMobile ? 2 : 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px' }}><Text type="secondary">No trend data available</Text></div>
            )}
          </Card>

          {/* Tier Performance Table */}
          {data.by_tier && data.by_tier.length > 0 && (
            <Card
              size="small"
              title={<Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>Revenue by Tier</Text>}
              style={{ marginBottom: '16px' }}
            >
              <Table
                dataSource={data.by_tier}
                rowKey="tier"
                size="small"
                pagination={false}
                columns={tierTableColumns}
                scroll={{ x: 'max-content' }}
              />
            </Card>
          )}

          {/* Customer Details Table */}
          <Card size="small" title={
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
              <Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>Customer Details</Text>
              {!isMobile && (
                <Text type="secondary" style={{ fontSize: '12px' }}>{data.summary?.date_range}</Text>
              )}
            </div>
          }>
            <Table
              columns={columns}
              dataSource={data.customers || []}
              rowKey="id"
              size="small"
              pagination={{
                defaultPageSize: isMobile ? 5 : 10,
                pageSizeOptions: ['5', '10', '20', '50'],
                showSizeChanger: !isMobile,
                showTotal: isMobile ? undefined : (total, range) => `${range[0]}-${range[1]} of ${total}`,
                simple: isMobile,
              }}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </>
      )}

      {!loading && !data && !error && (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Text type="secondary">No data available. Please adjust filters.</Text>
          </div>
        </Card>
      )}
    </div>
  );
};

export default CustomerRevenueReport;
